import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { WeightOption } from '@iheartjane/menu';
import { hideStrikethroughPrice } from '@iheartjane/menu';
import { PriceIdEnum } from '@iheartjane/types';
import type { PriceId } from '@iheartjane/types';

import { weightLabel } from '@jane/bloom/components/productCard/components/weightLabel';
import { WEIGHT_TO_GRAMS } from '@jane/bloom/dataAccess/constants';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import type { ShouldShowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { Cart } from '@jane/bloom/viewModels/schemas/checkout';

export interface BuildWeightOptionsArgs {
  cart?: Cart;
  maxWeightOption?: number | null;
  product: ProductSearchResultViewModel;
  smartProductInstance?: SmartSortProduct<MaterializedMenuProduct>;
  storeState?: string | null;
  weightFilter?: ShouldShowWeightFilter;
}

/**
 * Maps the product's available weights to weight selector options.
 * When `cart` and `storeId` are provided, each option is enriched with
 * `count` (current quantity in cart) and `maxQuantity` (total allowed).
 */
export const buildWeightOptions = ({
  cart,
  maxWeightOption,
  product,
  smartProductInstance,
  storeState,
  weightFilter,
}: BuildWeightOptionsArgs): WeightOption[] => {
  const filteredBySpecial =
    weightFilter && smartProductInstance
      ? product.available_weights.filter((weight) =>
          weightFilter(weight, smartProductInstance)
        )
      : product.available_weights;

  const hasWeightLimit = maxWeightOption != null;
  const availableWeights = hasWeightLimit
    ? filteredBySpecial.filter(
        (weight) => WEIGHT_TO_GRAMS[weight] <= maxWeightOption
      )
    : filteredBySpecial;

  const suppressStrikethrough = hideStrikethroughPrice(storeState ?? '');

  return availableWeights.reduce<WeightOption[]>((acc, rawWeight) => {
    const formatted = product.availableWeightsFormatted[rawWeight];
    if (!formatted) return acc;

    const priceId = formatWeight(rawWeight) as PriceId;
    if (!PriceIdEnum.includes(priceId)) return acc;

    const showStrikethrough =
      formatted.fullPriceFormatted !== formatted.priceFormatted &&
      !suppressStrikethrough;

    const cartItem = cart?.items.find(
      (item) =>
        item.productId === product.product_id && item.priceId === priceId
    );
    const currentCount = cartItem?.count ?? 0;
    const maxQuantity = getMaxCartQuantity({
      currentProduct: product,
      currentWeight: priceId,
      remaining: false,
    });

    acc.push({
      count: currentCount,
      isDiscounted: Boolean(product.special_id) && showStrikethrough,
      label: weightLabel(rawWeight),
      maxQuantity,
      originalPrice:
        product.special_amount && showStrikethrough
          ? formatted.fullPriceFormatted
          : undefined,
      potencyLabel: formatted.potencyLabResults,
      price: formatted.priceFormatted,
      value: priceId,
    });
    return acc;
  }, []);
};
